import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  ApiVersion,
  EMPTY_GLOBAL_CONFIG,
  GLOBAL_CONFIG_ID,
  GlobalService,
  IGlobalConfig,
  PerOrgPausableService
} from '@dadohr/shared/domainTypes/globalConfig';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { capitalize, snakeCase, sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  reshowAnnouncement,
  saveAnnouncement,
  useAnnouncement
} from '../../../services/announcement';
import { Loader } from '../../../components/Loader';
import { CanvasBar } from '../../../layout/Canvas';
import { Page } from '../../../layout/Page';
import { Section } from '../../../layout/Section';
import { useAllOrgs } from '../../services/orgs';
import {
  LoadingValue,
  store,
  useMappedLoadingValue
} from '../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import {
  getPausedOrgIds,
  PER_ORG_PAUSABLE_SERVICES,
  withOrgPaused
} from './disabledOrgs';

const toConfigDoc = generateToDocFn<IGlobalConfig>();

const configDocRef = () =>
  store().collection(FS.globalConfig).doc(GLOBAL_CONFIG_ID);

const cache = createDocumentListenerGetter<IGlobalConfig>(
  () => configDocRef(),
  toConfigDoc,
  EMPTY_GLOBAL_CONFIG
);

export const useGlobalConfig = (): LoadingValue<IGlobalConfig> => {
  return useMappedLoadingValue(useDocumentListener(cache('')), (d) => {
    const defaults = EMPTY_GLOBAL_CONFIG();
    if (!d) {
      return defaults;
    }
    return {
      ...defaults,
      ...d.data,
      services: {
        ...defaults.services,
        ...d.data.services
      },
      integrationVersions: {
        ...defaults.integrationVersions,
        ...(d.data.integrationVersions || {})
      },
      ai: d.data.ai,
      disabledOrgs: d.data.disabledOrgs
    };
  });
};

export const enableService = (key: GlobalService, value: boolean) => {
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const config = await t.get(ref);
    const d = (config.data() as IGlobalConfig) || EMPTY_GLOBAL_CONFIG();
    return t.set(ref, {
      ...d,
      services: {
        ...d.services,
        [key]: value
      }
    });
  });
};

/**
 * Per-org pause on top of the global kill-switch: adds/removes `orgId` in
 * `disabledOrgs.<service>`. Transactional so two superadmins toggling different
 * orgs at once can't drop each other's entry.
 */
export const setOrgServicePaused = (
  service: PerOrgPausableService,
  orgId: string,
  paused: boolean
) =>
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const config = await t.get(ref);
    const d = (config.data() as IGlobalConfig) || EMPTY_GLOBAL_CONFIG();
    return t.set(ref, {
      ...d,
      disabledOrgs: withOrgPaused(d.disabledOrgs, service, orgId, paused)
    });
  });

export const setIntegrationVersion = (integration: keyof IGlobalConfig['integrationVersions'], version: ApiVersion) => {
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const config = await t.get(ref);
    const d = (config.data() as IGlobalConfig) || EMPTY_GLOBAL_CONFIG();
    return t.set(ref, {
      ...d,
      integrationVersions: {
        ...d.integrationVersions,
        [integration]: version
      }
    });
  });
};

const Services = ({ config }: { config: IGlobalConfig }) => {
  const options: {
    key: GlobalService;
    label: string;
    checked: boolean;
  }[] = Object.entries(config.services).map(([key, checked]) => ({
    key: key as GlobalService,
    checked,
    label: snakeCase(key).split('_').map(capitalize).join(' ')
  }));

  return (
    <Section>
      <CanvasBar>Services</CanvasBar>
      <Card>
        <CardContent>
          <Table>
            <TableBody>
              {sortBy(options, (o) => o.key).map((o) => (
                <TableRow key={o.key}>
                  <TableCell>{o.label}</TableCell>
                  <TableCell align="right">
                    <Switch
                      checked={o.checked}
                      onChange={(ev) => enableService(o.key, ev.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};

const IntegrationVersions = ({ config }: { config: IGlobalConfig }) => {
  const options = Object.entries(config.integrationVersions).map(([key, version]) => ({
    key: key as keyof IGlobalConfig['integrationVersions'],
    version,
    label: snakeCase(key).split('_').map(capitalize).join(' ')
  }));

  return (
    <Section>
      <CanvasBar>Feature Versions</CanvasBar>
      <Card>
        <CardContent>
          <Table>
            <TableBody>
              {sortBy(options, (o) => o.key).map((o) => (
                <TableRow key={o.key}>
                  <TableCell>{o.label}</TableCell>
                  <TableCell align="right">
                    <ToggleButtonGroup
                      value={o.version}
                      exclusive
                      onChange={(_, newVersion) => newVersion && setIntegrationVersion(o.key, newVersion)}
                      size="small"
                    >
                      <ToggleButton value="v1">v1</ToggleButton>
                      <ToggleButton value="v2">v2</ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};

const FeatureAnnouncement = () => {
  const [current] = useAnnouncement();
  const [videoUrl, setVideoUrl] = useState('');
  const [title, setTitle] = useState('');
  const [active, setActive] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sync local fields whenever the stored announcement changes (e.g. after a save or
  // another admin's edit). Keyed on version so typing isn't clobbered mid-edit.
  useEffect(() => {
    setVideoUrl(current?.videoUrl ?? '');
    setTitle(current?.title ?? '');
    setActive(current?.active ?? false);
    // Sync only when the stored version changes — depending on the individual fields would
    // clobber the admin's in-progress edits on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.version]);

  const onSave = async () => {
    setSaving(true);
    try {
      await saveAnnouncement({ videoUrl, title, active });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section>
      <CanvasBar>Feature Announcement</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            One-time video modal shown to all dashboard admins. Saving bumps the version so
            it shows once to everyone; "Re-show to everyone" re-shows the current video
            without changing it. Current version: {current?.version ?? 0}
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Video URL (YouTube / Vimeo / Loom)"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || !videoUrl.trim()}
              onClick={onSave}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              disabled={saving || !current?.videoUrl}
              onClick={() => reshowAnnouncement()}
            >
              Re-show to everyone
            </Button>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
};

const PausedOrgs = ({ config }: { config: IGlobalConfig }) => {
  const [orgs] = useAllOrgs();
  const orgName = (orgId: string) =>
    orgs?.find((o) => o.id === orgId)?.data.name || orgId;

  return (
    <Section>
      <CanvasBar>Paused orgs</CanvasBar>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Orgs paused individually (set from each org's Dispatch section). Due
            items stay pending and send once the org is resumed. The global
            switches above still apply to every org.
          </Typography>
          <Table>
            <TableBody>
              {PER_ORG_PAUSABLE_SERVICES.map(({ key, label }) => {
                const orgIds = getPausedOrgIds(config.disabledOrgs, key);
                return (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>
                    <TableCell>
                      {orgIds.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          None
                        </Typography>
                      )}
                      {orgIds.map((orgId) => (
                        <div
                          key={orgId}
                          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                        >
                          <Link to={`/orgs/${orgId}`}>{orgName(orgId)}</Link>
                          <Button
                            size="small"
                            onClick={() => setOrgServicePaused(key, orgId, false)}
                          >
                            Resume
                          </Button>
                        </div>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Section>
  );
};

export const PageGlobalConfig = () => {
  const [config] = useGlobalConfig();
  return (
    <Page>
      {!config && <Loader />}
      {config && (
        <>
          <Services config={config} />
          <PausedOrgs config={config} />
          <IntegrationVersions config={config} />
          <FeatureAnnouncement />
        </>
      )}
    </Page>
  );
};
